import { endOfYesterday, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { useGetList, useTranslate } from "ra-core";
import type { ReactElement } from "react";
import { SearchInput } from "@/components/admin/search-input";

import { useCustomFieldFilterInputs } from "../customFields/customFieldFilterInputs";
import { ChoiceFilter } from "../filters/ChoiceFilter";
import {
  useSalesFilterChoices,
  useStarChoices,
} from "../filters/commonFilters";
import { NULL_FILTER_VALUE } from "../filters/listFilterValue";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Tag } from "../types";

/** Filters shown in the top bar until the user picks their own. */
export const DEFAULT_CONTACT_FILTERS = [
  "status@in",
  "lead_score@in",
  "sales_id@in",
  "tags@ov",
];

const MAX_TAGS = 100;

/** Every filter of the contacts list; « Ajouter un filtre » shows or hides them. */
export const useContactFilters = (): ReactElement[] => {
  const translate = useTranslate();
  const { noteStatuses } = useConfigurationContext();
  const starChoices = useStarChoices();
  const salesChoices = useSalesFilterChoices();
  const customFieldFilters = useCustomFieldFilterInputs("contacts");
  const { data: tags } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: MAX_TAGS },
    sort: { field: "name", order: "ASC" },
  });
  const now = new Date();
  const thisWeek = startOfWeek(now).toISOString();
  const thisMonth = startOfMonth(now).toISOString();

  return [
    <SearchInput
      key="q"
      source="q"
      alwaysOn
      placeholder={translate("resources.contacts.filters.search")}
    />,
    <ChoiceFilter
      key="status"
      source="status@in"
      label="resources.notes.fields.status"
      choices={noteStatuses}
    />,
    <ChoiceFilter
      key="lead_score"
      source="lead_score@in"
      label="resources.contacts.fields.lead_score"
      choices={starChoices}
    />,
    <ChoiceFilter
      key="sales_id"
      source="sales_id@in"
      label="resources.contacts.fields.sales_id"
      choices={salesChoices}
      exclusiveValue={NULL_FILTER_VALUE}
    />,
    <ChoiceFilter
      key="tags"
      source="tags@ov"
      label="resources.contacts.filters.tags"
      choices={(tags ?? []).map((tag) => ({
        value: String(tag.id),
        label: tag.name,
        color: tag.color,
      }))}
      mode="array"
    />,
    <ChoiceFilter
      key="recent_activity"
      source="last_seen@gte"
      label="crm.filters.recent_activity"
      mode="single"
      choices={[
        {
          value: endOfYesterday().toISOString(),
          label: translate("resources.contacts.filters.today"),
        },
        {
          value: thisWeek,
          label: translate("resources.contacts.filters.this_week"),
        },
        { value: thisMonth, label: translate("crm.filters.this_month") },
      ]}
    />,
    <ChoiceFilter
      key="inactive"
      source="last_seen@lte"
      label="crm.filters.inactive"
      mode="single"
      choices={[
        {
          value: thisWeek,
          label: translate("resources.contacts.filters.before_this_week"),
        },
        {
          value: thisMonth,
          label: translate("resources.contacts.filters.before_this_month"),
        },
        {
          value: subMonths(startOfMonth(now), 1).toISOString(),
          label: translate("resources.contacts.filters.before_last_month"),
        },
      ]}
    />,
    <ChoiceFilter
      key="tasks"
      source="nb_tasks@gt"
      label="resources.contacts.filters.tasks"
      mode="single"
      choices={[
        {
          value: "0",
          label: translate("resources.tasks.filters.with_pending"),
        },
      ]}
    />,
    ...customFieldFilters,
  ];
};
